import { useNotification } from 'admin';
import { type EvidenceTypeEnum, type EvidenceUploadMode } from 'api';
import { logger } from 'monitor';
import type { ChangeEvent } from 'react';
import { FilePicker } from 'ui';

import { ALLOWED_EVIDENCE_UPLOAD_FILE_TYPES } from '../../config';
import { useOfflineEvidence } from '../../hooks/useOfflineEvidence';

type Props = {
  mode: EvidenceUploadMode;
  evidenceType: EvidenceTypeEnum;
  parentId: string;
  onSuccess?: () => void;
  label?: string;
  metadata?: Record<string, unknown>;
  isOnline: boolean;
};

export const EvidenceUploadButton = ({
  onSuccess,
  mode,
  parentId,
  evidenceType,
  label = 'Upload',
  metadata,
  isOnline,
}: Props) => {
  const { open } = useNotification();
  const { mutateAsync: uploadEvidence, isPending } = useOfflineEvidence({
    isOnline,
    mode,
    mutationOptions: {
      onError: (error, { photo }: { photo: File }) => {
        open?.({
          message: `Failed to upload ${photo.name}: ${error}`,
          type: 'error',
        });
      },
      onSuccess: () => {
        onSuccess?.();
      },
    },
    parentId,
  });

  const handleUploadEvidence = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event?.target?.files;
    if (!files?.length) {
      logger.error('No file selected');
      return;
    }

    try {
      const uploadPromises = [];
      for (let i = 0; i < files.length; i++) {
        uploadPromises.push(
          uploadEvidence({
            evidenceType,
            metadata,
            parentId,
            photo: files[i],
            startTime: Date.now(),
          })
        );
      }

      await Promise.all(uploadPromises);

      open?.({
        message: 'Successfully uploaded evidence',
        type: 'success',
      });
    } catch (error) {
      logger.error('Failed to upload evidence', error as Error);
      open?.({
        message: 'Failed to upload evidence',
        type: 'error',
      });
    }
  };

  return (
    <FilePicker
      multiple
      accept={ALLOWED_EVIDENCE_UPLOAD_FILE_TYPES}
      onChange={handleUploadEvidence}
      label={label}
      buttonProps={{ fullWidth: true, loading: isPending }}
    />
  );
};
